import React, { useState, useEffect, useRef } from "react";
import { UIButton, UICheckBox, UIText } from "src/components/uIKit";
import styles from "./styles.module.scss";
import Separator from "src/components/separator";
import CardItem from "src/components/cardItem";
import masterCard from "src/assets/images/mastercard.png";
import visaCard from "src/assets/images/visa.png";
import { CardInfo, ErrorMessage, WarningBanner } from "src/components";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { setSecondSectionValid } from "src/redux/slices/appSlice/AppSlice";
import {
  API_RESULT,
  CHECKBOX_TYPE,
  COLLECT_CARD_INSTRUCTION,
  DIGITAL_AGENT_BUTTONS,
  POPUP_TYPE,
  RecordingState,
  ROUTES,
  STATE,
  TEXT_TAGS,
  URL_SOURCE_TYPE,
} from "src/utils/enums";
import { getIframeUrl, pausedActiveRecording } from "src/redux/slices/appSlice/AppApi";
import {
  __DEV__,
  getChannelId,
  getPaymentMethodIndicationFromCheckFxEligibility,
  isEnsureOrPfsRepOrPfsRepManual,
  logPfsAnalyticsEvent,
  sentPostMessage,
} from "src/utils/utils";
import { setPopupInfo } from "src/redux/slices/popupSlice/popupSlice";
import {
  CREDIT_CARD_BRAND,
  CreditCard,
  CreditCardStatus,
  PAYMENT_METHOD_INDICATION,
  UpdateOnboardingAnalytics,
} from "src/utils/types";
import { images } from "src/assets/images/images";
import { FormProvider, useForm } from "react-hook-form";
import getValidationRules from "src/validations/ValidationRules";
import {
  ACTIVATE_CARD_SERVICE_RATES_LINK,
  ACTIVATE_CARD_TERMS_OF_USE_LINK,
  FULL_URL,
  uiCustomData,
} from "src/utils/consts";
import {
  createOrUpdateUser,
  getCountries,
  getTaxRefundInfo,
  updateCCInfo,
} from "src/redux/slices/userSlice/UserApi";
import { useIsDigitalOrDigitalAgent } from "src/hooks/useIsDigitalOrDigitalAgent";
import { useCustomNavigate } from "src/hooks/useCustomNavigate";
interface ActivateCreditCardProps {
  onRefreshHeight?: () => void;
}

type ErrorText = { text: string; ariaLabel: string };

const formatExpirationDate = (date: string) => {
  // Handle YYYY-MM-DD format
  if (date && date.includes("-")) {
    const [, month, day] = date.split("-");
    return `${month}/${day}`;
  }
  // Keep existing logic for 4-digit format
  if (date && date.length === 4) {
    return `${date.slice(0, 2)}/${date.slice(2)}`;
  }
  return date;
};

const ActivateCreditCard: React.FC<ActivateCreditCardProps> = ({
  onRefreshHeight,
}) => {
  const methods = useForm();
  const {
    strings,
    currentState,
    iframeResStatus,
    iframeUrl,
    personalId,
    fXEligibilityData,
    travelPolicyNumber,
    businessUnit,
    guid,
    agentId,
    inquiryGCIframeResultsResStatus,
    hostUrl,
    selectedPolicy
  } = useAppSelector((state) => state.app);
  const {
    userInfo,
    creditCards,
    personalDetailsRes,
    updateCCInfoResStatus,
    updateCCInfoRes,
    createOrUpdateUserResStatus,
    countriesResStatus,
    taxRefundInfoResStatus,
    createOrUpdateUserRes,
    createOrUpdateUserError
  } = useAppSelector((state) => state.user);

  const dispatch = useAppDispatch();
  const validationRules = getValidationRules(strings);
  const { navigateTo } = useCustomNavigate();
  const isDigitalOrDigitalAgent = useIsDigitalOrDigitalAgent();
  const [iframeUri, setIframeUri] = useState("");
  const [isAcceptChecked, setIsAcceptChecked] = useState(false);
  const showCreditCardsSection = true;
  const [lastPaymentCardErrorText, setLastPaymentCardErrorText] = useState<
    ErrorText | string
  >("");

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [lastCard, setLastCard] = useState<CreditCard | null>(null);
  const [otherCards, setOtherCards] = useState<CreditCard[]>([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(0);
  const [cardAddedViaIframe, setCardAddedViaIframe] = useState(false);
  const [showCheckBox, setShowCheckBox] = useState(false);
  const [errorText, setErrorText] = useState<ErrorText | string>("");
  const watchedFields = methods.watch(["firstNameCard", "idCard"]);
  const currentSource = window.location.pathname
    .split("/")[1]
    .toLocaleLowerCase();
  // New card flow: when NOT in ensure/pfs-rep/rep-manual - always show iframe first and hide cards info
  const isNewCardFlow = !isEnsureOrPfsRepOrPfsRepManual(currentSource);
  const previousRecordingStateRef = useRef<RecordingState | null>(null);
  const iframeInitializedRef = useRef(false);

  const feServiceConditionConfirmation =
    personalDetailsRes?.feServiceConditionConfirmation;
  const activateCreditCardPoints = [
    {
      text: strings.t_activate_creditCard_point1,
      ariaLabel: strings.acc_t_activate_creditCard_point1,
    },
    {
      text: strings.t_activate_creditCard_point2,
      ariaLabel: strings.acc_t_activate_creditCard_point2,
    },
    {
      text: strings.t_activate_creditCard_point3,
      ariaLabel: strings.acc_t_activate_creditCard_point3,
    },
    {
      text: strings.t_activate_creditCard_point4,
      ariaLabel: strings.acc_t_activate_creditCard_point4,
    },
    {
      text: strings.t_activate_creditCard_point5,
      ariaLabel: strings.acc_t_activate_creditCard_point5,
    },
  ];
  const part1 = strings.t_activate_credit_card_termsOfService;
  const part2 = strings.t_activate_credit_card_servicePricing;

  const parts = strings.t_active_checkbox_label
    ? strings.t_active_checkbox_label.split(new RegExp(`(${part1}|${part2})`))
    : [];

  useEffect(() => {
    if (
      fXEligibilityData.paymentMethodIndication !==
      PAYMENT_METHOD_INDICATION.ACTIVE_CARD
    ) {
      setLastPaymentCardErrorText(
        getPaymentMethodIndicationFromCheckFxEligibility(
          fXEligibilityData.paymentMethodIndication,
          strings,
          isNewCardFlow ? false : otherCards.length > 0
        )
      );
    } else {
      setLastPaymentCardErrorText("");
    }
  }, [fXEligibilityData.paymentMethodIndication, isNewCardFlow, otherCards.length]);

  useEffect(() => {
    // After success  from get-iframe-result 
    // reset the error message from checkFxEligibility
    if (inquiryGCIframeResultsResStatus === API_RESULT.SUCCEEDED) {
      setLastPaymentCardErrorText("");
      setShowIframe(false);
      if (isNewCardFlow) {
        setCardAddedViaIframe(true);
      }
    }
  }, [inquiryGCIframeResultsResStatus, isNewCardFlow]);



  useEffect(() => {
    const isPfsRep = isEnsureOrPfsRepOrPfsRepManual(currentSource);
    setShowCheckBox(!isPfsRep && !feServiceConditionConfirmation);
    setIsAcceptChecked(!isPfsRep);
  }, [personalDetailsRes]);

  useEffect(() => {
    if (currentState != STATE.ACTIVATE_CREDIT_CARD) return;
    if (iframeInitializedRef.current) return;
    iframeInitializedRef.current = true;

    if (isNewCardFlow) {
      // NEW behavior: Always show iframe, no existing cards
      handleAddOtherPress();
    } else {
      // OLD behavior: Check if there are existing cards
      const userCreditCards = creditCards;
      if (userCreditCards && userCreditCards.length > 0) {
        // Find the active card
        const activeCard = userCreditCards.find(
          (card) => card.feCustCCStatus === CreditCardStatus.ACTIVE
        );
        // Get all non-active cards
        const nonActiveCards = userCreditCards.filter(
          (card) => card.feCustCCStatus !== CreditCardStatus.ACTIVE
        );

        // Add index 0 to active card
        const indexedActiveCard = activeCard ? { ...activeCard, index: 0 } : null;

        // Add sequential indices to non-active cards starting from 1
        const indexedNonActiveCards = nonActiveCards.map((card, idx) => ({
          ...card,
          index: idx + 1,
        }));

        setLastCard(indexedActiveCard);
        setOtherCards(indexedNonActiveCards);
        setSelectedCardIndex(0);
      } else {
        setLastCard(null);
        setOtherCards([]);
        handleAddOtherPress();
      }
    }
  }, [currentState, creditCards, isNewCardFlow]);

  // Update lastCard and otherCards when creditCards changes (for old flow)
  useEffect(() => {
    if (isNewCardFlow) return;
    if (!creditCards || creditCards.length === 0) return;

    // Find the active card
    const activeCard = creditCards.find(
      (card) => card.feCustCCStatus === CreditCardStatus.ACTIVE
    );
    // Get all non-active cards
    const nonActiveCards = creditCards.filter(
      (card) => card.feCustCCStatus !== CreditCardStatus.ACTIVE
    );

    // Add index 0 to active card
    const indexedActiveCard = activeCard ? { ...activeCard, index: 0 } : null;

    // Add sequential indices to non-active cards starting from 1
    const indexedNonActiveCards = nonActiveCards.map((card, idx) => ({
      ...card,
      index: idx + 1,
    }));

    setLastCard(indexedActiveCard);
    setOtherCards(indexedNonActiveCards);
  }, [creditCards, isNewCardFlow]);

  // Set lastCard only when a new card is added via iframe (for new card flow)
  useEffect(() => {
    if (!isNewCardFlow) return;
    if (cardAddedViaIframe && creditCards && creditCards.length > 0) {
      // Find the active card (most recently added)
      const activeCard = creditCards.find(
        (card) => card.feCustCCStatus === CreditCardStatus.ACTIVE
      );
      if (activeCard) {
        setLastCard({ ...activeCard, index: 0 });
      } else {
        // If no active card, use the first card
        setLastCard({ ...creditCards[0], index: 0 });
      }
    }
  }, [creditCards, cardAddedViaIframe, isNewCardFlow]);

  useEffect(() => {
    const isFpsRepOrRepManual =
      currentSource === URL_SOURCE_TYPE.PFS_REP ||
      currentSource === URL_SOURCE_TYPE.REP_MANUAL;

    const userParam = isFpsRepOrRepManual
      ? new URLSearchParams(window.location.search).get("user") || ""
      : "";

    const feSaleRepresentetiveName =
      currentSource === URL_SOURCE_TYPE.ENSURE
        ? selectedPolicy?.policyDtoField?.saleRepresentativeNameField
        : isFpsRepOrRepManual
          ? userParam
          : undefined;

    const newRecordingState =
      currentState === STATE.ACTIVATE_CREDIT_CARD && showIframe
        ? RecordingState.PAUSED
        : RecordingState.ACTIVE;

    // Only call the API if the recording state has changed
    if (previousRecordingStateRef.current !== newRecordingState) {
      // Skip dispatch on first render if newRecordingState is ACTIVE (1)
      const isFirstRender = previousRecordingStateRef.current === null;
      const shouldSkipDispatch = isFirstRender && newRecordingState === RecordingState.ACTIVE;
      const tempAgentAD = feSaleRepresentetiveName ? feSaleRepresentetiveName : getAgentByChannel(currentSource);

      if (!shouldSkipDispatch) {
        const payload = {
          feRecordingState: newRecordingState,
          feAgentAD: tempAgentAD?.replace(/^passportcard\\+/, ''),
        };
        dispatch(pausedActiveRecording(payload));
      }
      previousRecordingStateRef.current = newRecordingState;
    }

  }, [showIframe, currentState]);



  const getAgentByChannel = (channel: string) => {
    switch (channel) {

      case URL_SOURCE_TYPE.PFS_REP:
      case URL_SOURCE_TYPE.REP_MANUAL:
        return agentId ?? "";

      case URL_SOURCE_TYPE.ENSURE:
        return fXEligibilityData.policyDetails[0].policyDtoField.saleRepresentativeNameField;
      default:
        return "";



    }
  }

  useEffect(() => {
    const formValues = methods.getValues();
    const isPfsRep = isEnsureOrPfsRepOrPfsRepManual(currentSource);
    const isFormEmpty =
      showIframe && (!formValues.firstNameCard || !formValues.idCard);

    if (isNewCardFlow) {
      setButtonDisabled(
        (!isPfsRep && !isAcceptChecked) ||
        (showIframe && isFormEmpty)
      );
    } else {
      setButtonDisabled(
        (selectedCardIndex === null && !showIframe) ||
        (!isPfsRep && !isAcceptChecked) ||
        (showIframe && isFormEmpty)
      );
    }
  }, [
    selectedCardIndex,
    isAcceptChecked,
    showIframe,
    watchedFields,
    methods,
    userInfo?.email,
    isNewCardFlow,
  ]);



  useEffect(() => {
    if (updateCCInfoResStatus === API_RESULT.SUCCEEDED) {
      completeSubmit();
    } else if (updateCCInfoResStatus === API_RESULT.FAILED) {
      if (isDigitalOrDigitalAgent) {
        // send postMessage to parent
        const data = {
          buttonClicked:
            DIGITAL_AGENT_BUTTONS.OK_BUTTON_CLICKED_ON_CREDIT_CARD_SCREEN,
          errorMessage: updateCCInfoRes?.errorMessage,
          responseCode: "12",
          status: API_RESULT.FAILED,
          collectCardInstruction: fXEligibilityData.collectCardInstruction,
        };
        sentPostMessage(data);
      }
    }
  }, [updateCCInfoResStatus]);

  const completeSubmit = () => {
    dispatch(
      createOrUpdateUser({
        feCustPersId: personalId,
        feChannel: getChannelId(hostUrl),
        feCustTravelPolicyNumber: travelPolicyNumber,
        feBU: businessUnit,
        ...(showCheckBox && isAcceptChecked ? {
          feServiceConditionConfirmation: true,
        } : {}),
        feOperationModel: 2,
      })
    );
  };
  const updateDigitalAboutResponse = (responseCode: string, status: API_RESULT, errorMessage?: string) => {
    if (isDigitalOrDigitalAgent) {
      const data = {
        buttonClicked:
          DIGITAL_AGENT_BUTTONS.OK_BUTTON_CLICKED_ON_CREDIT_CARD_SCREEN,
        errorMessage: errorMessage || "",
        responseCode: responseCode,
        status: status,
        collectCardInstruction: fXEligibilityData.collectCardInstruction,
        passportCard4LastDigits:
          fXEligibilityData.collectCardInstruction ==
            COLLECT_CARD_INSTRUCTION.USE_EXISTING
            ? fXEligibilityData.passportCard4LastDigits
            : "",
      };
      sentPostMessage(data);
    }
  };
  useEffect(() => {
    if (createOrUpdateUserResStatus === API_RESULT.SUCCEEDED) {
      __DEV__ && console.log("createOrUpdateUserRes", createOrUpdateUserRes);
      if (createOrUpdateUserRes?.responseCode == "0") {
        if (isEnsureOrPfsRepOrPfsRepManual(currentSource)) {
          navigateTo(ROUTES.MUST_LIST_SCREEN);
        } else {
          dispatch(getCountries({}));
          dispatch(
            getTaxRefundInfo({
              feCustTravalPolicyNumber: travelPolicyNumber,
              feGuid: guid,
              feCustPersId: personalId,
              feBU: businessUnit,
            })
          );
        }
      } else {
        //show error message
        updateDigitalAboutResponse(createOrUpdateUserRes?.responseCode, API_RESULT.FAILED);
        setErrorText({
          text: strings.t_active_credit_card_error_message,
          ariaLabel: strings.acc_t_active_credit_card_error_message,
        });
      }
    } else if (createOrUpdateUserResStatus === API_RESULT.FAILED) {
      __DEV__ && console.log("createOrUpdateUserError", createOrUpdateUserError);
      // const errorMessage = createOrUpdateUserError?.responseMessage
      //   || createOrUpdateUserError?.data?.responseData?.responseMessage
      //   || createOrUpdateUserError?.data?.error?.message
      //   || (createOrUpdateUserError?.message && createOrUpdateUserError?.message == "Network Error") ? "401 Unauthorized" : "403 Forbidden or 401 Unauthorized";

      // const responseCode = createOrUpdateUserError?.data?.responseData?.responseCode?.toString() 
      // || createOrUpdateUserError?.status ||
      //   (createOrUpdateUserError?.message && createOrUpdateUserError?.message == "Network Error") ? ErrorResponseCode.TIMEOUT : ErrorResponseCode.GENERAL_ERROR;
      // updateDigitalAboutResponse(responseCode.toString(), API_RESULT.FAILED, errorMessage);
      setErrorText({
        text: strings.t_active_credit_card_error_message,
        ariaLabel: strings.acc_t_active_credit_card_error_message,
      });
    }


  }, [createOrUpdateUserResStatus]);

  useEffect(() => {
    if (
      countriesResStatus === API_RESULT.SUCCEEDED &&
      taxRefundInfoResStatus === API_RESULT.SUCCEEDED
    ) {
      updateDigitalAboutResponse("0", API_RESULT.SUCCEEDED);
      navigateTo(ROUTES.SUMMARY_SCREEN);
    }
  }, [countriesResStatus, taxRefundInfoResStatus]);

  const handleCheckboxChange = (index: number) => {
    setSelectedCardIndex(index);
  };
  const handleAcceptCheckboxChange = () => {
    setIsAcceptChecked(!isAcceptChecked);
  };
  const onSubmit = () => {
    if (isNewCardFlow) {
      completeSubmit();
    } else {
      if (selectedCardIndex == 1 || selectedCardIndex == 2) {
        const updateCCInfoData = {
          feCustCCToken: otherCards[selectedCardIndex - 1].feCCToken,
          feCustCCStatus:
            otherCards[selectedCardIndex - 1].feCustCCStatus ==
              CreditCardStatus.INACTIVE
              ? 1
              : 2,
          feCustPersId: personalId,
          feCustTravelPolicyNumber: travelPolicyNumber,
          feBU: businessUnit,
        };
        dispatch(updateCCInfo(updateCCInfoData));
      } else {
        completeSubmit();
      }
    }
    dispatch(setSecondSectionValid(true));
  };
  // const swapCardWithLastCard = (index: number) => {
  //   const card = otherCards.find((card) => card.index === index);
  //   if (card && lastCard) {
  //     // Create a new array with updated indices
  //     const newOtherCards = otherCards
  //       .filter((c) => c.index !== index)
  //       .map((c) => ({
  //         ...c,
  //         // Keep indices sequential
  //         index: c.index > index ? c.index - 1 : c.index
  //       }));

  //     // Update lastCard with the selected card, maintaining index 0
  //     setLastCard({...card, index: 0});

  //     // Add previous lastCard to other cards with proper index
  //     setOtherCards([{...lastCard, index: 1}, ...newOtherCards]);
  //     //set selected card index to 0
  //     setSelectedCardIndex(0);
  //   }
  // };

  const handleAddOtherPress = () => {
    setShowIframe(!showIframe);
    setTimeout(() => {
      onRefreshHeight && onRefreshHeight();
    });
    if (!showIframe) {
      const currentPath = window.location.pathname; // This will get the path part after the domain
      const data = {
        personalId,
        jsonConfig: uiCustomData,
        failureRedirectUrl: `${FULL_URL}${currentPath}/failure`,
        successRedirectUrl: `${FULL_URL}${currentPath}/success`,
        businessUnit
      };
      dispatch(getIframeUrl(data));
    }
  };
  useEffect(() => {
    if (iframeResStatus === API_RESULT.SUCCEEDED) {
      setTimeout(() => {
        setIframeUri(iframeUrl);
      }, 1000);
    } else if (iframeResStatus === API_RESULT.FAILED) {
      setIframeUri(iframeUrl);
    }
  }, [iframeResStatus]);

  const showFaqPopup = () => {
    logPfsAnalyticsEvent({
      activate_credit_card_qa: true,
    } as UpdateOnboardingAnalytics);

    dispatch(
      setPopupInfo({
        show: true,
        type: POPUP_TYPE.FAQ,
        title: strings.t_faq_credit_card_title,
        subTitle: strings.t_faq_credit_card_sub_title,
        body: strings.t_faq_credit_card_body,
        img: images.image5,
        mobileImg: images.image5Mobile,
        okBtnText: strings.t_faq_credit_card_button,
        hideCloseBtn: false,
        strings: {
          questionsTitle: strings.t_faq,
          questionOne: strings.t_faq_credit_card_question_one,
          answerOne: strings.t_faq_credit_card_answer_one,
          questionTwo: strings.t_faq_credit_card_question_two,
          answerTwo: strings.t_faq_credit_card_answer_two,
          questionThree: strings.t_faq_credit_card_question_three,
          answerThree: strings.t_faq_credit_card_answer_three,
          questionFour: strings.t_faq_credit_card_question_four,
          answerFour: strings.t_faq_credit_card_answer_four,
          questionFive: strings.t_faq_credit_card_question_five,
          answerFive: strings.t_faq_credit_card_answer_five,
        },
      })
    );
  };
  const renderPolicyText = (parts: string[]) => {
    return parts?.map((part, index) => {
      if (part === part1) {
        return (
          <a
            key={index}
            href={ACTIVATE_CARD_TERMS_OF_USE_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      }
      if (part === part2) {
        return (
          <a
            key={index}
            href={ACTIVATE_CARD_SERVICE_RATES_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      }
      return part; // Render normal text
    });
  };

  return (
    <FormProvider {...methods}>
      <div className={styles.container}>
        <UIText
          text={strings.t_active_credit_how_it_works}
          tag={TEXT_TAGS.H3}
          size={20}
          weight={500}
          style={styles.mBottom}
        />
        <ul className={styles.activateCreditCardPoints}>
          {activateCreditCardPoints.map((point, index) => (
            <li key={index}>
              <UIText
                text={point.text}
                ariaLabel={point.ariaLabel}
                size={18}
                weight={400}
                style={styles.sectionTitle}
              />
            </li>
          ))}
        </ul>

        <label id="aria-label-text" htmlFor="link-popup" className="srOnly">
          {strings.acc_t_active_credit_more_info}
        </label>
        <div
          id="activate-credit-card-link-popup"
          tabIndex={0}
          aria-label={strings.acc_t_active_credit_more_info}
          onClick={showFaqPopup}
          onKeyUp={(e) =>
            e.key === "Enter" || e.key === " " ? showFaqPopup() : null
          }
          style={{ cursor: "pointer" }}
        >
          <UIText
            text={strings.t_active_credit_more_info}
            ariaLabel={strings.acc_t_active_credit_more_info}
            size={18}
            weight={400}
            style={styles.link}
          />
          <img src={images.link} alt="arrow" className={styles.leftArrow} />
        </div>
        <Separator />
        <UIText
          text={strings.t_active_credit_choose_payment_method}
          ariaLabel={strings.acc_t_active_credit_choose_payment_method}
          tag={TEXT_TAGS.H3}
          size={20}
          weight={500}
          style={styles.mBottom}
        />
        {fXEligibilityData.isRiskCountry && (
          <WarningBanner
            title={strings.t_active_credit_banner_title}
            ariaLabel={
              strings.acc_t_active_credit_banner_title +
              " " +
              strings.acc_t_active_credit_banner_sub_title +
              " " +
              strings.acc_t_active_credit_banner_sub_title_second_part
            }
            tag={TEXT_TAGS.H4}
            subTitle={strings.t_active_credit_banner_sub_title}
            subTitleSecondPart={
              strings.t_active_credit_banner_sub_title_second_part
            }
          />
        )}
        {showCreditCardsSection ? (
          <>
            {lastCard && (
              <>
                <div>
                  <UIText
                    text={strings.t_active_credit_last_card}
                    size={18}
                    weight={500}
                    style={styles.sectionTitle}
                  />

                  <CardItem
                    title={strings.t_active_credit_last_card}
                    ariaLabel={strings.acc_t_active_credit_last_card}
                    key={lastCard.index}
                    image={
                      lastCard.feCCCardBrand == CREDIT_CARD_BRAND.MASTERCARD
                        ? masterCard
                        : visaCard
                    }
                    digits={lastCard.feCC4LastDigits}
                    expirationDate={formatExpirationDate(
                      lastCard.feCustCCExpirationDate
                    )}
                    index={lastCard.index}
                    isChecked={
                      isNewCardFlow
                        ? true
                        : lastCard.feCustCCStatus == CreditCardStatus.ACTIVE &&
                        selectedCardIndex == 0
                    }
                    onCheckboxChange={isNewCardFlow ? () => { } : handleCheckboxChange}
                  />
                </div>
                <Separator />
              </>
            )}

            {lastPaymentCardErrorText &&
              fXEligibilityData.paymentMethodIndication !==
              PAYMENT_METHOD_INDICATION.ACTIVE_CARD && (
                <ErrorMessage
                  style={styles.errorStyleZip}
                  ariaLabel={
                    (lastPaymentCardErrorText as ErrorText).ariaLabel
                  }
                  message={
                    (lastPaymentCardErrorText as ErrorText).text
                  }
                />
              )}

            {!isNewCardFlow && otherCards.length > 0 && <Separator />}
            {!isNewCardFlow && (
              <div>
                {otherCards.length > 0 && (
                  <UIText
                    text={strings.t_active_credit_saved_cards}
                    size={18}
                    weight={500}
                    style={styles.sectionTitle}
                  />
                )}
                {otherCards.map((card) => (
                  <CardItem
                    title={strings.t_active_credit_saved_cards}
                    key={card.index}
                    image={
                      card.feCCCardBrand == CREDIT_CARD_BRAND.MASTERCARD
                        ? masterCard
                        : visaCard
                    }
                    digits={card.feCC4LastDigits}
                    expirationDate={formatExpirationDate(
                      card.feCustCCExpirationDate
                    )}
                    index={card.index}
                    isChecked={
                      card.feCustCCStatus == CreditCardStatus.INACTIVE &&
                      selectedCardIndex == card.index
                    }
                    onCheckboxChange={handleCheckboxChange}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <ErrorMessage
            style={styles.errorStyleZip}
            ariaLabel={strings.acc_t_active_credit_card_error}
            message={strings.t_active_credit_card_error}
          />
        )}

        <div>
          {(isNewCardFlow ? cardAddedViaIframe : creditCards?.length > 0) && (
            <div className={styles.mTop} onClick={handleAddOtherPress}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
                tabIndex={0}
                onKeyUp={(e) =>
                  e.key === "Enter" ? handleAddOtherPress() : null
                }
                aria-expanded={showIframe}
              >
                <UIText
                  text={isNewCardFlow ? strings.t_active_credit_add_other_payment : strings.t_active_credit_replace_other_payment}
                  ariaLabel={strings.acc_t_active_credit_add_other_payment}
                  size={16}
                  weight={400}
                  style={styles.link}
                />
                <img
                  src={showIframe ? images.up : images.down}
                  alt="arrow"
                  className={styles.arrow}
                />
              </div>
            </div>
          )}
          <div className={styles.mBottomCard} />
          {showIframe && (
            <CardInfo
              strings={strings}
              validationRules={validationRules}
              iframeUrl={iframeUri}
              showTitle={isNewCardFlow ? cardAddedViaIframe : creditCards?.length > 0}
            />
          )}

          <div
            className={styles.checkBoxWrapper}
            aria-description={
              strings.acc_t_active_credit_accept +
              " " +
              strings.acc_t_active_credit_terms +
              " " +
              strings.acc_t_active_credit_checkbox_text
            }
          >
            {!isEnsureOrPfsRepOrPfsRepManual(currentSource) && showCheckBox && (
              <>
                <UICheckBox
                  checked={isAcceptChecked}
                  onChange={handleAcceptCheckboxChange}
                  type={CHECKBOX_TYPE.SQUARE}
                />
                <UIText text={renderPolicyText(parts)} size={18} weight={400} />
              </>
            )}
          </div>
        </div>

        <div className={styles.buttonWrapepr}>
          {errorText && (
            <>
              <ErrorMessage
                style={styles.errorStyleZip}
                ariaLabel={(errorText as ErrorText).ariaLabel}
                message={(errorText as ErrorText).text}
              />
              <br />
            </>
          )}
          <UIButton
            title={strings.t_active_credit_next}
            onSubmit={onSubmit}
            isLoading={
              updateCCInfoResStatus === API_RESULT.LOADING ||
              createOrUpdateUserResStatus === API_RESULT.LOADING
            }
            disabled={
              buttonDisabled ||
              updateCCInfoResStatus === API_RESULT.LOADING ||
              createOrUpdateUserResStatus === API_RESULT.LOADING ||
              updateCCInfoResStatus === API_RESULT.SUCCEEDED ||
              createOrUpdateUserResStatus === API_RESULT.SUCCEEDED
            }
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default ActivateCreditCard;
